import React, { useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Badge, Card, Form, Spinner, Table } from 'react-bootstrap';
import {
  ADMIN_CHAT_RECIPIENTS,
  ADMIN_CHAT_SENT_MESSAGES,
  DELETE_ADMIN_CHAT_MESSAGES,
  SEND_ADMIN_CHAT_MESSAGE,
} from './graphql';

// Mirrors ADMIN_CHAT_MESSAGE_MAX_LENGTH / ADMIN_CHAT_MESSAGE_MAX_RECIPIENTS in the backend
// (src/common/concerns/chat/config.ts) so the sender gets feedback before the round trip.
const MAX_CONTENT_LENGTH = 1000;
const MAX_RECIPIENTS = 2000;

// One of the AdminChatMessageStyle values from chat/admin/typedefs.ts. The app picks its layout from
// the type of the sender, so the style is really a choice of sender: no client update is involved.
// Only BUBBLE is offered here - it is sent by the official account, so it gets a real chat bubble with
// an avatar, proper wrapping and clickable links. SYSTEM still exists on the backend but renders as a
// centered caption, which reads badly for anything longer than one line.
const STYLE = 'BUBBLE';

type Recipient = {
  id: string;
  displayName: string;
  picture: string | null;
  userLevel: number | null;
};

type AdminChatRecipientsQuery = {
  adminChatRecipients: {
    users: Recipient[];
    invalidUserIds: string[];
  };
};

type SendAdminChatMessageMutation = {
  sendAdminChatMessage: {
    sentCount: number;
    invalidUserIds: string[];
    failedUserIds: string[];
  };
};

type SentMessage = {
  messageId: string;
  userId: string;
  content: string;
  sent: string;
  style: string;
};

type AdminChatSentMessagesQuery = {
  adminChatSentMessages: {
    messages: SentMessage[];
    invalidUserIds: string[];
  };
};

type DeleteAdminChatMessagesMutation = {
  deleteAdminChatMessages: {
    deletedCount: number;
    failed: Array<{ userId: string; messageId: string; reason: string }>;
  };
};

/** messageId is only unique inside one recipient's conversation, so the pair is the real identity. */
const refKey = ({ userId, messageId }: { userId: string; messageId: string }) => `${userId}.${messageId}`;

const SendChatMessage = () => {
  const [userIdsText, setUserIdsText] = useState('');
  const [content, setContent] = useState('');
  const [sendResult, setSendResult] = useState<SendAdminChatMessageMutation['sendAdminChatMessage'] | null>(null);
  const [selectedRefs, setSelectedRefs] = useState<Record<string, SentMessage>>({});
  const [deleteResult, setDeleteResult] = useState<
    DeleteAdminChatMessagesMutation['deleteAdminChatMessages'] | null
  >(null);

  const userIds = useMemo(
    () =>
      userIdsText
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    [userIdsText]
  );
  const uniqueUserIds = useMemo(() => Array.from(new Set(userIds)), [userIds]);

  const [loadRecipients, { data: recipientsData, loading: recipientsLoading, error: recipientsError }] =
    useLazyQuery<AdminChatRecipientsQuery>(ADMIN_CHAT_RECIPIENTS, { fetchPolicy: 'network-only' });
  const [sendMessage, { loading: sendLoading, error: sendError }] =
    useMutation<SendAdminChatMessageMutation>(SEND_ADMIN_CHAT_MESSAGE);
  const [loadSentMessages, { data: sentData, loading: sentLoading, error: sentError }] =
    useLazyQuery<AdminChatSentMessagesQuery>(ADMIN_CHAT_SENT_MESSAGES, { fetchPolicy: 'network-only' });
  const [deleteMessages, { loading: deleteLoading, error: deleteError }] =
    useMutation<DeleteAdminChatMessagesMutation>(DELETE_ADMIN_CHAT_MESSAGES);

  const recipients = recipientsData?.adminChatRecipients;
  const sentMessages = sentData?.adminChatSentMessages.messages ?? [];
  const selectedList = Object.values(selectedRefs);
  const isBusy = recipientsLoading || sendLoading || sentLoading || deleteLoading;
  const tooManyRecipients = uniqueUserIds.length > MAX_RECIPIENTS;
  const contentTooLong = content.trim().length > MAX_CONTENT_LENGTH;
  const canSend =
    uniqueUserIds.length > 0 && content.trim().length > 0 && !tooManyRecipients && !contentTooLong && !isBusy;

  const resetResults = () => {
    if (sendResult) {
      setSendResult(null);
    }
    if (deleteResult) {
      setDeleteResult(null);
    }
  };

  const handlePreview = () => {
    resetResults();
    loadRecipients({ variables: { userIds: uniqueUserIds } });
  };

  const handleSend = () => {
    const shouldContinue = window.confirm(
      `Send this message to ${uniqueUserIds.length} user(s)? It appears immediately in their SongPop chat.`
    );
    if (!shouldContinue) {
      return;
    }

    setSendResult(null);
    sendMessage({ variables: { input: { userIds: uniqueUserIds, content: content.trim(), style: STYLE } } })
      .then((res) => {
        if (res.data) {
          setSendResult(res.data.sendAdminChatMessage);
        }
      })
      .catch(() => {
        // The error is surfaced through sendError below.
      });
  };

  const handleLoadSent = () => {
    resetResults();
    // A stale selection would point at rows that are no longer on screen.
    setSelectedRefs({});
    loadSentMessages({ variables: { userIds: uniqueUserIds } });
  };

  const toggleSelected = (message: SentMessage) => {
    setSelectedRefs((current) => {
      const next = { ...current };
      if (next[refKey(message)]) {
        delete next[refKey(message)];
      } else {
        next[refKey(message)] = message;
      }
      return next;
    });
  };

  const handleDelete = () => {
    const shouldContinue = window.confirm(
      `Delete ${selectedList.length} message(s)? They disappear from the recipients' chat. ` +
        'Anyone who already read a message keeps what they saw, only the history goes away.'
    );
    if (!shouldContinue) {
      return;
    }

    setDeleteResult(null);
    deleteMessages({
      variables: {
        input: { messages: selectedList.map(({ userId, messageId }) => ({ userId, messageId })) },
      },
    })
      .then((res) => {
        if (res.data) {
          setDeleteResult(res.data.deleteAdminChatMessages);
          setSelectedRefs({});
          // Re-read so the table reflects what is actually left rather than the pre-delete list.
          loadSentMessages({ variables: { userIds: uniqueUserIds } });
        }
      })
      .catch(() => {
        // The error is surfaced through deleteError below.
      });
  };

  return (
    <div className="d-flex flex-column">
      <h1>Send Chat Message</h1>

      {recipientsError ? <Alert variant="danger">{recipientsError.message}</Alert> : null}
      {sendError ? <Alert variant="danger">{sendError.message}</Alert> : null}
      {sentError ? <Alert variant="danger">{sentError.message}</Alert> : null}
      {deleteError ? <Alert variant="danger">{deleteError.message}</Alert> : null}

      {deleteResult ? (
        <>
          <Alert variant="success">{deleteResult.deletedCount} message(s) deleted.</Alert>
          {deleteResult.failed.length > 0 ? (
            <Alert variant="warning">
              {deleteResult.failed.length} message(s) were left untouched:
              <ul className="mb-0">
                {deleteResult.failed.map((failure) => (
                  <li key={refKey(failure)}>
                    <code>{failure.messageId}</code> for <code>{failure.userId}</code> &mdash; {failure.reason}
                  </li>
                ))}
              </ul>
            </Alert>
          ) : null}
        </>
      ) : null}

      {sendResult ? (
        <>
          <Alert variant="success">Message sent to {sendResult.sentCount} user(s).</Alert>
          {sendResult.invalidUserIds.length > 0 ? (
            <Alert variant="warning">
              {sendResult.invalidUserIds.length} unknown id(s), nothing was sent to them:{' '}
              {sendResult.invalidUserIds.join(', ')}
            </Alert>
          ) : null}
          {sendResult.failedUserIds.length > 0 ? (
            <Alert variant="danger">
              {sendResult.failedUserIds.length} delivery failure(s), these users received nothing and can be retried:{' '}
              {sendResult.failedUserIds.join(', ')}
            </Alert>
          ) : null}
        </>
      ) : null}

      <Card className="mb-3">
        <Card.Body>
          <Form.Group controlId="userIds">
            <Form.Label>User ids</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              value={userIdsText}
              disabled={isBusy}
              isInvalid={tooManyRecipients}
              onChange={(event) => {
                setUserIdsText(event.currentTarget.value);
                resetResults();
              }}
            />
            <Form.Control.Feedback type="invalid">
              Too many recipients, the maximum is {MAX_RECIPIENTS} per send. Split the list.
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Separate ids with commas, spaces, or new lines. {uniqueUserIds.length} unique id(s) detected
              {userIds.length !== uniqueUserIds.length ? ` (${userIds.length - uniqueUserIds.length} duplicate(s))` : ''}
              .
            </Form.Text>
          </Form.Group>

          <button
            type="button"
            className="btn btn-outline-secondary mb-3"
            disabled={uniqueUserIds.length === 0 || isBusy}
            onClick={handlePreview}
          >
            {recipientsLoading ? <Spinner animation="border" size="sm" /> : 'Preview recipients'}
          </button>

          {recipients ? (
            <>
              {recipients.invalidUserIds.length > 0 ? (
                <Alert variant="warning">
                  {recipients.invalidUserIds.length} unknown id(s), they will receive nothing:{' '}
                  {recipients.invalidUserIds.join(', ')}
                </Alert>
              ) : null}
              <p>
                <Badge variant="success">{recipients.users.length} will receive the message</Badge>
              </p>
              {recipients.users.length > 0 ? (
                <Table striped bordered size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Level</th>
                      <th>User id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.users.map((user) => (
                      <tr key={user.id}>
                        <td>{user.displayName}</td>
                        <td>{user.userLevel}</td>
                        <td>
                          <code>{user.id}</code>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : null}
            </>
          ) : null}

          <Form.Group controlId="content" className="mb-0">
            <Form.Label>Message</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={content}
              disabled={isBusy}
              isInvalid={contentTooLong}
              onChange={(event) => {
                setContent(event.currentTarget.value);
                resetResults();
              }}
            />
            <Form.Control.Feedback type="invalid">
              The message is too long, the maximum is {MAX_CONTENT_LENGTH} characters.
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              {content.trim().length}/{MAX_CONTENT_LENGTH} characters.
            </Form.Text>
          </Form.Group>

          <button type="button" className="btn btn-primary mt-3" disabled={!canSend} onClick={handleSend}>
            {sendLoading ? <Spinner animation="border" size="sm" /> : `Send to ${uniqueUserIds.length} user(s)`}
          </button>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <Card.Title as="h5">Delete sent messages</Card.Title>
          <Card.Subtitle className="mb-3 text-muted">
            Uses the same user ids above. Only messages sent from this screen can be deleted.
          </Card.Subtitle>

          <button
            type="button"
            className="btn btn-outline-secondary mb-3"
            disabled={uniqueUserIds.length === 0 || isBusy}
            onClick={handleLoadSent}
          >
            {sentLoading ? <Spinner animation="border" size="sm" /> : 'Load sent messages'}
          </button>

          {sentData ? (
            <>
              {sentMessages.length === 0 ? (
                <Alert variant="info">No admin message found for these users.</Alert>
              ) : (
                <>
                  <Table striped bordered size="sm" responsive>
                    <thead>
                      <tr>
                        <th style={{ width: '1%' }} />
                        <th>Sent</th>
                        <th>Recipient</th>
                        <th>Look</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentMessages.map((message) => (
                        <tr key={refKey(message)}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              aria-label={`Select message ${message.messageId}`}
                              checked={Boolean(selectedRefs[refKey(message)])}
                              disabled={isBusy}
                              onChange={() => toggleSelected(message)}
                            />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{new Date(message.sent).toLocaleString()}</td>
                          <td>
                            <code>{message.userId}</code>
                          </td>
                          <td>{message.style}</td>
                          <td style={{ whiteSpace: 'pre-wrap' }}>{message.content}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={selectedList.length === 0 || isBusy}
                    onClick={handleDelete}
                  >
                    {deleteLoading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      `Delete ${selectedList.length} selected message(s)`
                    )}
                  </button>
                </>
              )}
            </>
          ) : null}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SendChatMessage;
